/**
 * Core Durak game rules and logic
 * Implements attack/defend mechanics, card comparison, and game flow
 */

import type { Card, Suit, PlaySlot, Rank } from '../types/game';

/**
 * Determines if a defender card beats an attacker card
 *
 * Rules:
 * 1. Same suit + higher rank = beats
 * 2. Trump suit beats any non-trump
 * 3. Non-trump cannot beat trump
 */
export function cardBeats(
  attacker: Card,
  defender: Card,
  trump: Suit
): boolean {
  // Same suit: higher rank wins
  if (defender.suit === attacker.suit) {
    return defender.value > attacker.value;
  }

  // Trump beats non-trump
  if (defender.suit === trump) {
    return true;
  }

  // Non-trump cannot beat trump or different suit
  return false;
}

/**
 * Determines if an attack card can be added to the play zone
 *
 * Rules:
 * 1. First attack: any card is valid
 * 2. Subsequent attacks: rank must already be in play zone
 * 3. Cannot exceed 6 cards total
 */
export function canAttack(
  playZone: readonly PlaySlot[],
  card: Card,
  maxCards: number = 6
): boolean {
  // First attack always valid
  if (playZone.length === 0) {
    return true;
  }

  // Check max cards limit
  if (playZone.length >= maxCards) {
    return false;
  }

  // Check if rank is already in play
  const ranksInPlay = new Set<Rank>();
  for (const slot of playZone) {
    ranksInPlay.add(slot.attacker.rank);
    if (slot.defender) {
      ranksInPlay.add(slot.defender.rank);
    }
  }

  return ranksInPlay.has(card.rank);
}

/**
 * Determines if a defender card can be played against a specific attacker
 *
 * Rules:
 * 1. Must beat the attacker card (same suit higher rank, or trump)
 * 2. Can only defend open slots (no existing defender)
 */
export function canDefend(
  attacker: Card,
  defender: Card,
  trump: Suit
): boolean {
  return cardBeats(attacker, defender, trump);
}

/**
 * Finds the first open slot in the play zone
 */
export function findOpenSlot(
  playZone: readonly PlaySlot[]
): PlaySlot | undefined {
  return playZone.find(slot => slot.defender === null);
}

/**
 * Gets all open slots that need defending
 */
export function getOpenSlots(
  playZone: readonly PlaySlot[]
): PlaySlot[] {
  return playZone.filter(slot => slot.defender === null);
}

/**
 * Checks if all attacks have been defended
 */
export function allSlotsDefended(
  playZone: readonly PlaySlot[]
): boolean {
  if (playZone.length === 0) return false;
  return playZone.every(slot => slot.defender !== null);
}

/**
 * Determines if the game has ended.
 *
 * Podkidnoy Durak rule: the first player to empty their hand wins; the player
 * left holding cards is the "Durak" (Fool). The game can only end once the
 * draw deck is exhausted.
 */
export function checkGameOver(
  deckLength: number,
  playerHandLength: number,
  botHandLength: number
): {
  ended: boolean;
  playerWon: boolean;
  reason: 'player_empty' | 'bot_empty' | 'both_empty' | 'ongoing';
} {
  if (deckLength > 0) {
    return { ended: false, playerWon: false, reason: 'ongoing' };
  }

  const playerEmpty = playerHandLength === 0;
  const botEmpty = botHandLength === 0;

  // Simultaneous empty = draw; treat as a non-loss for the human player.
  if (playerEmpty && botEmpty) {
    return { ended: true, playerWon: true, reason: 'both_empty' };
  }

  // Player emptied first → player wins, bot is the Durak.
  if (playerEmpty) {
    return { ended: true, playerWon: true, reason: 'player_empty' };
  }

  // Bot emptied first → bot wins, player is the Durak.
  if (botEmpty) {
    return { ended: true, playerWon: false, reason: 'bot_empty' };
  }

  return { ended: false, playerWon: false, reason: 'ongoing' };
}

/**
 * Determines the loser (Durak/Fool) of the game
 * The player with cards remaining when the other is empty loses
 */
export function determineLoser(
  playerHandLength: number,
  botHandLength: number
): 'player' | 'bot' | null {
  if (playerHandLength === 0 && botHandLength > 0) return 'bot';
  if (botHandLength === 0 && playerHandLength > 0) return 'player';
  if (playerHandLength === 0 && botHandLength === 0) return null; // Draw
  return null; // Game not over
}

/**
 * Collects all cards from the play zone
 * Used when defender takes cards or bout ends
 */
export function collectPlayZoneCards(
  playZone: readonly PlaySlot[]
): Card[] {
  const cards: Card[] = [];
  for (const slot of playZone) {
    cards.push(slot.attacker);
    if (slot.defender) {
      cards.push(slot.defender);
    }
  }
  return cards;
}

/**
 * Validates a complete game state
 * Returns true if state is valid for gameplay
 */
export function validateGameState(state: {
  deck: readonly Card[];
  playerHand: readonly Card[];
  botHand: readonly Card[];
  playZone: readonly PlaySlot[];
  trump: Suit;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check total card count (should be 36)
  const totalCards =
    state.deck.length +
    state.playerHand.length +
    state.botHand.length +
    state.playZone.length * 2; // Approximate, doesn't account for undefended

  if (totalCards !== 36) {
    errors.push(`Invalid card count: ${totalCards} (expected 36)`);
  }

  // Check trump is valid
  if (!['♠', '♥', '♦', '♣'].includes(state.trump)) {
    errors.push(`Invalid trump suit: ${state.trump}`);
  }

  // Check play zone doesn't exceed max
  if (state.playZone.length > 6) {
    errors.push(`Play zone exceeds max: ${state.playZone.length} > 6`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Gets valid attack cards from a hand
 * Returns cards that can legally be played as attacks
 */
export function getValidAttackCards(
  hand: readonly Card[],
  playZone: readonly PlaySlot[]
): Card[] {
  if (playZone.length === 0) {
    // First attack: any card is valid
    return [...hand];
  }

  // Get ranks in play
  const ranksInPlay = new Set<Rank>();
  for (const slot of playZone) {
    ranksInPlay.add(slot.attacker.rank);
    if (slot.defender) {
      ranksInPlay.add(slot.defender.rank);
    }
  }

  // Return cards with matching ranks
  return hand.filter(card => ranksInPlay.has(card.rank));
}

/**
 * Gets valid defense cards against a specific attacker
 */
export function getValidDefenseCards(
  hand: readonly Card[],
  attacker: Card,
  trump: Suit
): Card[] {
  return hand.filter(card => canDefend(attacker, card, trump));
}

/**
 * Calculates the optimal defense card (lowest valid card)
 */
export function findOptimalDefense(
  hand: readonly Card[],
  attacker: Card,
  trump: Suit
): Card | null {
  const validCards = getValidDefenseCards(hand, attacker, trump);

  if (validCards.length === 0) return null;

  // Prefer non-trump defenders to conserve trumps for harder defends.
  // Within each group, pick the lowest value.
  return [...validCards].sort((a, b) => {
    const aTrump = a.suit === trump ? 1 : 0;
    const bTrump = b.suit === trump ? 1 : 0;
    if (aTrump !== bTrump) return aTrump - bTrump;
    return a.value - b.value;
  })[0];
}

/**
 * Formats a play slot for display
 */
export function formatPlaySlot(slot: PlaySlot): string {
  const attacker = `${slot.attacker.rank}${slot.attacker.suit}`;
  const defender = slot.defender
    ? `${slot.defender.rank}${slot.defender.suit}`
    : '?';
  return `${attacker} → ${defender}`;
}

/**
 * Game action result types
 */
export interface GameActionResult {
  success: boolean;
  error?: string;
  stateChange?: {
    type: 'card_played' | 'cards_taken' | 'bout_ended' | 'game_ended';
    data: unknown;
  };
}
